import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * A custom Angular pipe that sanitizes and safely renders raw HTML content.
 *
 * This pipe uses Angular's `DomSanitizer` to bypass security and trust the HTML content
 * as safe. It is useful when you want to bind raw HTML to the DOM using `[innerHTML]`
 * without Angular stripping it out.
 *
 * ⚠️ Use with caution: Ensure the HTML content is from a trusted source to avoid XSS vulnerabilities.
 *
 * ### Example usage in template:
 * ```html
 * <div [innerHTML]="htmlContent | safeHtml"></div>
 * ```
 */
@Pipe({
  name: 'safeHtml',
  standalone: true,
})
export class SafeHtmlPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  /**
   * Transforms a raw HTML string into a trusted `SafeHtml` object.
   *
   * @param value - The raw HTML string to sanitize.
   * @returns A `SafeHtml` object that Angular can safely bind to the DOM.
   */
  transform(value: string): SafeHtml {
    const cleaned = value.trim().replace(/\n+$/, '');
    return this.sanitizer.bypassSecurityTrustHtml(cleaned);
  }
}
